import { useMemo, type FC, type JSX } from "react";
import { Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { usePrefetchCalendar } from "utils/hooks/usePrefetchCalendar";
import { useWeeksCount } from "utils/hooks/useWeeksCount";

import StrawberrySVG from "images/icons/strawberry.svg";
import TrainingSVG from "images/icons/training.svg";
import WaterDropSVG from "images/icons/waterDrop.svg";
import { useAppSelector } from "store/hooks";
import { getDaysFulfillsPlan, getElixitOfLife, getSessionsAchieved } from "store/slices/user";

interface Props {
  withLink?: boolean;
  className?: string;
  isShort?: boolean;
}

type StreakType = "elixir" | "strawberry" | "training" | "weeks";

const Streaks: Record<StreakType, { icon: JSX.Element; translationKey: string; cssClass: string }> = {
  elixir: {
    icon: <WaterDropSVG />,
    translationKey: "streak.elixir",
    cssClass: "streak--elixir",
  },
  strawberry: {
    icon: <StrawberrySVG />,
    translationKey: "streak.strawberry",
    cssClass: "streak--strawberry",
  },
  training: {
    icon: <TrainingSVG />,
    translationKey: "streak.training",
    cssClass: "streak--training",
  },
  weeks: {
    icon: <Image src={"/images/30-calendar-D-day.svg"} loading={"eager"} />,
    translationKey: "streak.weeks",
    cssClass: "streak--weeks",
  },
};

const CurrentStreak: FC<Props> = ({ withLink = true, className, isShort = false }) => {
  const { t } = useTranslation(["common"]);
  const { weeksCount } = useWeeksCount();
  const { preloadChunk, prefetchCalendar } = usePrefetchCalendar();

  const elixirOfLife = useAppSelector(getElixitOfLife);
  const daysFulfillsPlan = useAppSelector(getDaysFulfillsPlan);
  const sessionsAchieved = useAppSelector(getSessionsAchieved);

  const streakItem = (type: StreakType, count?: number | null) => {
    const { icon: Icon, translationKey, cssClass } = Streaks[type];

    return (
      <span className={`streak ${cssClass}`}>
        <span className="streak__content">
          <span className="streak__icon">{Icon}</span>
          <span className="streak__badge">
            {count ?? 0}
            {type === "weeks" ? "" : "x"}
          </span>
        </span>
        {isShort ? null : <span className="streak__title">{t(translationKey).toLocaleLowerCase()}</span>}
      </span>
    );
  };

  const renderStreaks = useMemo(
    () => (
      <span className="streaks-wrapper">
        {streakItem("elixir", elixirOfLife)}
        {streakItem("strawberry", daysFulfillsPlan)}
        {streakItem("training", sessionsAchieved)}
        {isShort ? null : streakItem("weeks", weeksCount)}
      </span>
    ),
    [elixirOfLife, daysFulfillsPlan, sessionsAchieved, weeksCount],
  );

  if (withLink) {
    return (
      <div className={className}>
        <Link
          to={"/kalendar"}
          className="streak__link"
          onPointerDown={prefetchCalendar}
          onMouseEnter={preloadChunk}
          onFocus={prefetchCalendar}
        >
          {renderStreaks}
        </Link>
      </div>
    );
  }

  return <div className={className}>{renderStreaks}</div>;
};

export default CurrentStreak;
